import React, { Component, createRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Col,
  Button,
} from "reactstrap";
import { func } from "prop-types";
import { Formik } from "formik";
import swal from "sweetalert2";
import keyholdersApi from "../../../api/keyholder";
import { createScryptHash, aesDecrypt } from "../crypto-utils";
import { handleError as originalHandleError } from "@/util/errorHandler";
import { getSecretKeySchema } from "@/util/validationHelper";

const handleError = error =>
  originalHandleError(error, { title: "An error occured" });

const SecretKeySchema = getSecretKeySchema();

const BACKUP_OPTION = "backup";
const MANUAL_OPTION = "manual";

class SecretKeyForm extends Component {
  static propTypes = {
    onSubmit: func.isRequired,
  };

  static defaultProps = {
    onSubmit: () => {},
  };

  state = {
    selectedOption: BACKUP_OPTION,
    backupText: "",
    isSubmitting: false,
  };

  formik = createRef();

  handleSelectBackup = () => this.setState({ selectedOption: BACKUP_OPTION });

  handleSelectManually = () => this.setState({ selectedOption: MANUAL_OPTION });

  handleFileSelect = ({
    target: {
      files: [backupFile],
    },
  }) => {
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      if (target.readyState !== 2) return;
      if (target.error) {
        return handleError(target.error);
      }

      this.setState({ backupText: target.result });
    };

    reader.readAsText(backupFile);
  };

  handleSubmit = async event => {
    event.preventDefault();

    const { selectedOption } = this.state;

    if (selectedOption === BACKUP_OPTION) {
      this.handleSubmitFromBackup();
    }

    if (selectedOption === MANUAL_OPTION) {
      this.handleSubmitFromManual();
    }
  };

  handleSubmitFromBackup = async () => {
    try {
      const { backupText } = this.state;

      if (!backupText) {
        swal("Backup error", "Please select a backup file", "warning");
      } else {
        this.setState({ isSubmitting: true });

        const { aeskey, salt } = await keyholdersApi.getSaltAndAeskey();
        const secretKey = aesDecrypt(backupText, aeskey);

        if (!secretKey) throw new Error("Can't decrypt backup file");

        const hash = await createScryptHash(secretKey, salt);

        this.props.onSubmit(hash);
      }
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleSubmitFromManual = async () => {
    try {
      const errors = await this.formik.current.validateForm();

      if (!errors.secretKey && !errors.secretKeyRepeat) {
        this.setState({ isSubmitting: true });

        const { secretKey } = this.formik.current.state.values;
        const { salt } = await keyholdersApi.getSaltAndAeskey();
        const hash = await createScryptHash(secretKey, salt);

        this.props.onSubmit(hash);
      }
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { selectedOption, backupText, isSubmitting } = this.state;

    return (
      <Card>
        <Formik
          initialValues={{
            secretKey: "",
            secretKeyRepeat: "",
          }}
          validationSchema={SecretKeySchema}
          validate={values => {
            if (selectedOption === BACKUP_OPTION) return {};
            if (values.secretKey !== values.secretKeyRepeat) {
              return {
                secretKeyRepeat: "Secret keys are not matched",
              };
            }
            return {};
          }}
          ref={this.formik}>
          {({ values, handleChange, handleBlur, errors, touched }) => (
            <Form>
              <CardHeader>Please input your secret key</CardHeader>
              <CardBody style={{ paddingBottom: 0, height: 175 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    width: "50%",
                    margin: "auto",
                  }}>
                  <Label check>
                    <Input
                      type="radio"
                      onClick={this.handleSelectBackup}
                      readOnly
                      checked={selectedOption === BACKUP_OPTION}
                    />
                    Select from backup file
                  </Label>
                  <Label check>
                    <Input
                      type="radio"
                      onClick={this.handleSelectManually}
                      readOnly
                      checked={selectedOption === MANUAL_OPTION}
                    />
                    Enter manually
                  </Label>
                </div>
                {selectedOption === BACKUP_OPTION && (
                  <div style={{ marginTop: 15 }}>
                    <FormGroup>
                      <Label sm={9}>
                        Backup
                        <Input
                          type="text"
                          name="backup"
                          value={backupText}
                          disabled
                        />
                      </Label>
                      <Label
                        className="btn btn-secondary"
                        style={{ width: 150, height: 35, marginTop: 5 }}>
                        Select backup file
                        <Input
                          type="file"
                          name="backupFile"
                          accept=".priv"
                          style={{ visibility: "hidden" }}
                          value={undefined}
                          onChange={this.handleFileSelect}
                        />
                      </Label>
                      {errors.secretKey ? (
                        <FormFeedback className="text-alert">
                          {errors.secretKey}
                        </FormFeedback>
                      ) : (
                        <div style={{ height: 20 }} />
                      )}
                    </FormGroup>
                  </div>
                )}
                {selectedOption === MANUAL_OPTION && (
                  <div style={{ marginTop: 15 }}>
                    <FormGroup row style={{ marginBottom: 0 }}>
                      <Label sm={3}>Secret key</Label>
                      <Col sm={9}>
                        <Input
                          type="password"
                          name="secretKey"
                          invalid={Boolean(errors.secretKey)}
                          value={values.secretKey}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.secretKey ? (
                          <FormFeedback className="text-alert">
                            {errors.secretKey}
                          </FormFeedback>
                        ) : (
                          <div style={{ height: 20 }} />
                        )}
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Label sm={3}>Repeat secret key</Label>
                      <Col sm={9}>
                        <Input
                          type="password"
                          name="secretKeyRepeat"
                          invalid={Boolean(errors.secretKeyRepeat)}
                          value={values.secretKeyRepeat}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.secretKeyRepeat && touched.secretKeyRepeat ? (
                          <FormFeedback>{errors.secretKeyRepeat}</FormFeedback>
                        ) : (
                          <div style={{ height: 20 }} />
                        )}
                      </Col>
                    </FormGroup>
                  </div>
                )}
              </CardBody>
              <CardFooter style={{ textAlign: "center" }}>
                <Button
                  type="submit"
                  color="success"
                  onClick={this.handleSubmit}
                  disabled={isSubmitting}>
                  Submit
                </Button>
              </CardFooter>
            </Form>
          )}
        </Formik>
      </Card>
    );
  }
}

export default SecretKeyForm;
